import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate, Outlet, useParams, useSearchParams, useNavigate } from "react-router-dom";

const LandingPage = React.lazy(() => import("./pages/landing/LandingPage"));
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import { useAuth } from "./contexts/AuthContext";
import { ContentProvider, useContent } from "./contexts/ContentContext";
import { AudioProvider } from "./contexts/AudioContext";
import { TranslationProvider } from "./i18n";
import AudioPlayer from "./components/ui/AudioPlayer";
import { listWeddings } from "./lib/api";
import { detectWeddingSubdomain } from "./lib/subdomain";

// Pages
import NotFound from "pages/NotFound";
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import MyWeddings from "./pages/dashboard/MyWeddings";
import OnboardingFlow from "./pages/onboarding";
import AdminDashboard from "./pages/admin-dashboard";
import RSVPManagement from "./pages/rsvp-management";
import WeddingInvitationDisplay from "./pages/wedding-invitation-display";
import GuestGallery from "./pages/guest-gallery";
import WeddingSchedule from "./pages/wedding-schedule";
import WeddingInfo from "./pages/wedding-info";
import CheckinPage from "./pages/checkin";
import InstagramCallback from "./pages/instagram-callback";
const SuperAdminDashboard = React.lazy(() => import("./pages/superadmin/SuperAdminDashboard"));
const SuperAdminLoginPage = React.lazy(() => import("./pages/auth/SuperAdminLoginPage"));
const ForgotPasswordPage = React.lazy(() => import("./pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = React.lazy(() => import("./pages/auth/ResetPasswordPage"));

// ---------------------------------------------------------------------------
// ProtectedRoute -- redirects unauthenticated users to /login
// ---------------------------------------------------------------------------
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// ---------------------------------------------------------------------------
// LegacyAdminRedirect -- redirects /admin-dashboard?tab=X to /dashboard/weddings/:id?tab=X
// ---------------------------------------------------------------------------
const LegacyAdminRedirect = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const tab = searchParams.get('tab');
    listWeddings(token)
      .then((res) => {
        const weddings = res.weddings || res;
        if (weddings.length === 1) {
          const qs = tab ? `?tab=${tab}` : '';
          navigate(`/dashboard/weddings/${weddings[0].id}${qs}`, { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      })
      .catch(() => navigate('/dashboard', { replace: true }));
  }, [token, searchParams, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  return null;
};

// ---------------------------------------------------------------------------
// DashboardRouter -- shows SuperAdmin dashboard for superadmins, MyWeddings for others
// ---------------------------------------------------------------------------
const DashboardRouter = () => {
  const { user } = useAuth();
  if (user?.role === 'superadmin') {
    return (
      <Suspense fallback={<div className="min-h-screen" />}>
        <SuperAdminDashboard />
      </Suspense>
    );
  }
  return <MyWeddings />;
};

// ---------------------------------------------------------------------------
// WeddingPage -- provides slug-specific content for public wedding routes
// ---------------------------------------------------------------------------
const WeddingPageInner = () => {
  const { content } = useContent();
  const lang = content?.settings?.language || 'en';
  return (
    <TranslationProvider lang={lang}>
      <AudioProvider>
        <Outlet />
        <AudioPlayer />
      </AudioProvider>
    </TranslationProvider>
  );
};

const WeddingPage = () => {
  const { slug } = useParams();
  return (
    <ContentProvider slug={slug}>
      <WeddingPageInner />
    </ContentProvider>
  );
};

// ---------------------------------------------------------------------------
// SubdomainWeddingPage -- serves wedding content when accessed via subdomain
// ---------------------------------------------------------------------------
const SubdomainWeddingPage = ({ subdomain }) => {
  return (
    <ContentProvider subdomain={subdomain}>
      <WeddingPageInner />
    </ContentProvider>
  );
};

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------
const Routes = () => {
  const subdomain = detectWeddingSubdomain();

  // If on a wedding subdomain, render wedding routes at root
  if (subdomain) {
    return (
      <BrowserRouter>
        <ErrorBoundary>
          <ScrollToTop />
          <RouterRoutes>
            <Route path="/" element={<SubdomainWeddingPage subdomain={subdomain} />}>
              <Route index element={<WeddingInvitationDisplay />} />
              <Route path="rsvp" element={<RSVPManagement />} />
              <Route path="gallery" element={<GuestGallery />} />
              <Route path="schedule" element={<WeddingSchedule />} />
              <Route path="info" element={<WeddingInfo />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </ErrorBoundary>
      </BrowserRouter>
    );
  }

  // Normal routing (main domain)
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Public landing page */}
          <Route path="/" element={<Suspense fallback={<div className="min-h-screen" />}><LandingPage /></Suspense>} />

          {/* Auth */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/superadmin" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#B8860B] border-t-transparent rounded-full animate-spin" /></div>}><SuperAdminLoginPage /></Suspense>} />
          <Route path="/forgot-password" element={<Suspense fallback={<div className="min-h-screen" />}><ForgotPasswordPage /></Suspense>} />
          <Route path="/reset-password" element={<Suspense fallback={<div className="min-h-screen" />}><ResetPasswordPage /></Suspense>} />

          {/* Protected */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingFlow />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardRouter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/weddings/:id"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* Legacy redirect -- old /admin-dashboard now lives at /dashboard/weddings/:id */}
          <Route
            path="/admin-dashboard"
            element={
              <ProtectedRoute>
                <LegacyAdminRedirect />
              </ProtectedRoute>
            }
          />

          {/* Public wedding routes -- WeddingPage provides slug-specific content */}
          <Route path="/w/:slug" element={<WeddingPage />}>
            <Route index element={<WeddingInvitationDisplay />} />
            <Route path="rsvp" element={<RSVPManagement />} />
            <Route path="gallery" element={<GuestGallery />} />
            <Route path="schedule" element={<WeddingSchedule />} />
            <Route path="info" element={<WeddingInfo />} />
          </Route>

          {/* Utility */}
          <Route path="/checkin" element={<CheckinPage />} />
          <Route path="/instagram-callback" element={<InstagramCallback />} />

          {/* Catch-all */}
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
